import React, { useContext, useMemo, useState } from 'react';

import styled from 'styled-components';

import GridProvider from 'contextProviders/GridProvider';
import BaseViewsContext from 'contexts/BaseViewsContext';
import Row from 'components/Common/Row';
import FilterPlates from 'components/FilterPlates';
import ResetReportButton from 'components/Report/ResetReportButton';
import RecordsGrid from 'components/Grid/RecordsGrid';
import DateInput from 'components/DateInput';
import Inputs from 'components/Inputs';
import Tooltip from 'components/Tooltip';
import Button from 'kingpin/atoms/Button';
import isDefined from 'isDefined';

import DatasetTrendButton from './DatasetTrendButton';
import DATASET_TREND_BUTTON from './DatasetTrendButton/constants';
import DefaultFiltersToggle from '../../../components/DefaultFiltersToggle';

const Content = styled.div`
  width: 100%;

  display: flex;
  flex: 1;
  flex-direction: column;
  overflow: hidden;
`;

const SearchScreenDefaultFiltersToggle = ({
  dataType,
}: {
  dataType: string;
}) => {
  const relevantDataTypes = useMemo(() => {
    return [dataType];
  }, [dataType]);

  return <DefaultFiltersToggle relevantDataTypes={relevantDataTypes} />;
};

const SearchScreen = ({
  dataType,
  datasetOptions,
  hasLeftDefaultState,
  isDatasetSelectDisabled,
}: {
  dataType: string;
  datasetOptions: DropdownOption[];
  hasLeftDefaultState: boolean;
  isDatasetSelectDisabled: boolean;
}) => {
  const { baseViews } = useContext(BaseViewsContext);
  const baseView = baseViews[dataType];

  // Transient field filter selection (field names for the current dataType).
  // Empty means "show all default columns". Resets on dataset change because
  // SearchScreen is mounted with key={dataType} in the container.
  const [selectedFields, setSelectedFields] = useState<string[]>([]);

  const baseViewOverride = useMemo((): FleetOps.BaseView | undefined => {
    if (selectedFields.length === 0 || !baseView) {
      return undefined;
    }

    const selected = new Set(selectedFields);
    const fields: FleetOps.BaseView['fields'] = {};
    Object.values(baseView.fields)
      .filter(isDefined)
      .forEach((field) => {
        fields[field.field] = {
          ...field,
          isVisible: selected.has(field.field),
        };
      });

    return { ...baseView, fields };
  }, [baseView, selectedFields]);

  return (
    <div
      style={{
        padding: '16px 24px',
        flex: 1,
        // Without it the screen grows to the grid's content (a flex item's
        // min-height is its content's), so DataTable's grid never scrolls
        // and keeps loading pages
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Row style={{ marginBottom: 16 }} spaceBetween>
        <Row
          centerAlign
          style={{
            flexWrap: 'wrap',
            gap: 8,
          }}
        >
          <DateInput />
          <FilterPlates />
          <SearchScreenDefaultFiltersToggle dataType={dataType} />
        </Row>
        <Row centerAlign>
          {hasLeftDefaultState && <ResetReportButton />}
          <div style={{ marginRight: 8 }}>
            <Inputs.Dropdown
              options={datasetOptions}
              isDisabled={isDatasetSelectDisabled}
            />
          </div>
          <DatasetTrendButton dataset={dataType} />
          <div style={{ marginLeft: 8 }}>
            <Inputs.FieldsPicker
              selectedFields={[{ dataType, fields: selectedFields }]}
              onFieldsConfirmed={(confirmed) =>
                setSelectedFields(
                  confirmed.find((c) => c.dataType === dataType)?.fields ?? [],
                )
              }
              datasetsGreenList={[dataType]}
              isPerformanceDatasetsAllowed
              hideDatasetSidebar
              showClearButton
              applyOnChange
              Button={({ onClick }) => (
                <Tooltip content="Select displayed fields">
                  <Button
                    type={selectedFields.length > 0 ? 'Selected' : 'Secondary'}
                    size="Small"
                    icon="eye"
                    onClick={onClick}
                    testId="search-field-filter-button"
                  />
                </Tooltip>
              )}
            />
          </div>
        </Row>
      </Row>
      <Content>
        <div id={DATASET_TREND_BUTTON.TREND_DIV_ID} />
        <div
          style={{
            // The space the trend leaves, and no more
            flex: 1,
            minHeight: 0,
          }}
        >
          <GridProvider dataType={dataType} baseViewOverride={baseViewOverride}>
            <RecordsGrid
              surface="search"
              // AG leaves every column at its default width, resizable
              sizing="fixed"
              isResizable
              layoutOnFirstRender={false}
              layoutOnColumnChange={false}
              layoutOnModelUpdated={false}
              hasBorders={true}
              hasDarkHeader={true}
              isSearchScreen
            />
          </GridProvider>
        </div>
      </Content>
    </div>
  );
};

export default SearchScreen;
